import { useState } from "react";
import OrganizationPicker from "@components/OrganizationPicker/OrganizationPicker";
import { EngagementService } from "@services/engagements/engagementService";
import { LaunchService } from "@services/launches/LaunchService";
import { workflowEvents } from "src/modules/engagement/workflow/workflowEvents";
import type { Launch } from "@models/launch.model";
import type { PopulationSegment } from "@models/populationSegment.model";
import ModalPopulationChangeWarning from "./ModalPopulationChangeWarning";
import PublishersCard from "./PublishersCard";
import DirectMailCard from "./DirectMailCard";
import EmailCard from "./EmailCard";
import SmsCard from "./SmsCard";
import PopulationSection from "./Population/PopulationSection";
import UseCaseField from "./UseCaseField";
import { RecordId } from "@/shared/components/recordId/RecordId";
import { CampaignDatesCard } from "./CampaignDatesCard";
import CampaignKeyDatesCard from "./CampaignKeyDatesCard";

type Props = {
  campaign: Launch;
  clientOrg: { id: number; name: string };
  segments: PopulationSegment[];
  objectiveName: string;
  onBack: () => void;
  onCampaignChanged: (campaign: Launch) => void;
};

export default function CampaignDetail({
  campaign,
  clientOrg,
  segments,
  objectiveName,
  onBack,
  onCampaignChanged,
}: Props) {
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Segment change awaiting the user's confirmation in the warning modal.
  const [pending, setPending] = useState<
    { segmentId: number | null; explicitNone: boolean } | null
  >(null);

  // "No population" as an active choice (lookalike / DB-fed) — distinct from
  // "Not selected", so linking health knows this campaign is not a gap.
  const EXPLICIT_NONE = "none";

  const handleOrgChange = async (organizationId: number | null) => {
    if (organizationId == null) return;
    setIsSaving(true);
    setError(null);
    try {
      const updated = await new LaunchService().linkOrganization(campaign.id, organizationId);
      onCampaignChanged(updated);
      workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
    } catch {
      setError("Could not change the sub-organization. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const applySegment = async (segmentId: number | null, explicitNone: boolean) => {
    setIsSaving(true);
    setError(null);
    try {
      const updated = await new EngagementService().setLaunchPopulationSegment(
        campaign.id,
        segmentId,
        explicitNone,
      );
      onCampaignChanged(updated);
      workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
    } catch (err) {
      // The server refuses only when this campaign's audience members are live
      // on ad networks — a state the user must resolve by unpublishing, not by
      // retrying — so surface its message instead of a generic "try again".
      setError(
        err instanceof Error && err.message
          ? err.message
          : "Could not change the population segment. Try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  /*
      Populations are shared across campaigns by design, so picking one that is
      in use is allowed — but it gets a warning, not silence. Same for changing
      or clearing an already-set population. Only a first-time pick of an unused
      population goes through without the modal.
  */
  const currentValue =
    campaign.population_segment_id ??
    (campaign.population_segment_explicit_none ? EXPLICIT_NONE : "");

  const handleSegmentChange = (value: string) => {
    if (value === String(currentValue)) return;
    const explicitNone = value === EXPLICIT_NONE;
    const segmentId = value && !explicitNone ? Number(value) : null;
    const target = segmentId != null ? segments.find((s) => s.id === segmentId) : null;
    const needsConfirmation =
      (target?.launch_count ?? 0) > 0 || campaign.population_segment_id != null;
    if (needsConfirmation) {
      setPending({ segmentId, explicitNone });
      return;
    }
    void applySegment(segmentId, explicitNone);
  };

  const pendingSegment =
    pending?.segmentId != null ? segments.find((s) => s.id === pending.segmentId) ?? null : null;

  return (
    <div className="campaign-pane">
      <div className="campaign-head">
        <button type="button" className="campaign-head__back" onClick={onBack}>
          ‹ Campaigns
        </button>
        <h5>{campaign.name}</h5>
        <RecordId label="Campaign" id={campaign.id} />
        {/* Which objective this campaign serves — the list groups by it, so the
            detail states it too rather than losing the context on the way in. */}
        <span className="campaign-head__objective">{objectiveName}</span>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      <CampaignDatesCard campaign={campaign} onChanged={onCampaignChanged} />

      <CampaignKeyDatesCard launchId={campaign.id} />

      <div className="campaign-label">Settings</div>
      <div className="campaign-card campaign-settings">
        <div>
          <div className="campaign-label">Sub-organization</div>
          <OrganizationPicker
            value={campaign.organizations?.[0]?.id ?? clientOrg.id}
            onChange={handleOrgChange}
            root={clientOrg}
            disabled={isSaving}
          />
        </div>
        <div>
          <div className="campaign-label">Population segment</div>
          <select
            className="form-select"
            value={currentValue}
            onChange={(e) => handleSegmentChange(e.target.value)}
            disabled={isSaving}
          >
            <option value="">Not selected</option>
            <option value={EXPLICIT_NONE}>No population — lookalike / DB-fed</option>
            {segments.map((segment) => (
              <option key={segment.id} value={segment.id}>
                {segment.name}
                {segment.launch_count > 0 && segment.id !== campaign.population_segment_id
                  ? ` — in use by ${segment.launch_count} campaign${segment.launch_count === 1 ? "" : "s"}`
                  : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Its own row under the two above, per the MPN-1689 mockup: those are about who the
          campaign reaches, this is about what it is for. */}
      <UseCaseField launchId={campaign.id} />

      <div className="mt-4">
        <PublishersCard launchId={campaign.id} />
      </div>

      {/* Direct mail sits beside the publishers rather than among them: it has no ad
          account and no template, and it needs a return address, which no network row has
          (MPN-1706). */}
      <div className="mt-4">
        <DirectMailCard launchId={campaign.id} organizationId={clientOrg?.id ?? null} />

        <EmailCard
          launchId={campaign.id}
          engagementId={campaign.engagement_id ?? 0}
          organizationId={clientOrg?.id ?? null}
        />

        <SmsCard
          launchId={campaign.id}
          engagementId={campaign.engagement_id ?? 0}
          organizationId={clientOrg?.id ?? null}
        />
      </div>

      {/* The people this campaign runs on, and each one's journey. Lives here
          because the population is a property of the campaign — the segment
          picker above sets it, the control-group holdout is configured per
          campaign, and the journey endpoint is launch-scoped. Collapsed by
          default; it is a drill-down, not the point of the page. */}
      <div className="mt-4">
        <PopulationSection launchId={campaign.id} />
      </div>

      <ModalPopulationChangeWarning
        show={pending !== null}
        targetName={pendingSegment?.name ?? null}
        sharedCount={pendingSegment?.launch_count ?? 0}
        changingExisting={campaign.population_segment_id != null}
        isSaving={isSaving}
        onConfirm={() => {
          if (pending === null) return;
          const { segmentId, explicitNone } = pending;
          setPending(null);
          void applySegment(segmentId, explicitNone);
        }}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}
